// Mochi-voiced notification phrase library + dispatcher.
//
// Every OS-level notification flows through `fireMochiNotification(type, ctx)`.
// The title is ALWAYS the lowercase string "mochi 🍡" — never the app name,
// never uppercase. Bodies come from rotating phrase banks below so the user
// hears Mochi, not an app.
//
// Rotation per type is anti-repeat (never the same phrase back-to-back),
// persisted in localStorage so reloads don't break the cadence.

// ---------------------------------------------------------------------------
// Phrase banks — copy is the source of truth, treat as constants.
// Add new variants here over time. Templates may include `{task_title}`.
// ---------------------------------------------------------------------------
export const MOCHI_PHRASES = {
    // ─── Reminder: initial due ring ───────────────────────────────────────
    reminder_due: [
        "hey 🍡 — {task_title}? no rush, just a nudge.",
        "psst… {task_title} is waiting for you.",
        "friendly reminder from your pal — {task_title} 👀",
        "hey, you mentioned {task_title}… want to knock it out?",
        "i remembered {task_title} so you don't have to 🍡",
    ],
    // ─── Reminder: 15-min heads-up "pre" alert ────────────────────────────
    reminder_pre: [
        "heads up — {task_title} in about 15 min 🌸",
        "in a bit: {task_title}. just so you can wind down whatever you're in.",
        "soft warning: {task_title} coming up 🍡",
    ],
    // ─── Reminder: first auto-snooze retry (20 min after a miss) ──────────
    // Phrase bank is ready for when that re-firing logic ships.
    reminder_retry_1: [
        "no worries — still here about {task_title}. whenever you're ready.",
        "hey, {task_title} again. take your time 🌸",
        "just checking in — {task_title} can wait a little longer.",
    ],
    // ─── Reminder: second auto-snooze retry (final) ───────────────────────
    reminder_retry_2: [
        "{task_title} one more time. after this i'll let it rest 🍡",
        "last gentle nudge — {task_title}. you've got this whenever.",
    ],

    // ─── Periodic check-ins — rotated by notification mode ───────────────
    checkin_gentle: [
        "hey… how's your day going? mochi's curious 🌸",
        "just popping in. everything okay?",
        "a little check-in from me to you 🍡",
    ],
    checkin_balanced: [
        "hey! quick check-in time. how are the tasks going?",
        "mochi here — want to do a little review together?",
    ],
    checkin_accountability: [
        "okay, let's do this. check-in time 🎯",
        "hey — accountability buddy calling. how's it going?",
    ],

    // ─── Accountability ping (task-specific 2h nag) ──────────────────────
    accountability_ping: [
        "still on {task_title}? we can do this in 5 🎯",
        "hey — {task_title} is still open. one tiny step?",
        "your accountability pal here. {task_title}?",
    ],

    // ─── Morning check-in nudge (if not done by 9am) ─────────────────────
    morning_nudge: [
        "good morning 🍡 ready to plan our day together?",
        "hey sleepyhead — when you're ready, let's look at today.",
        "morning! i saved your spot from yesterday 🌸",
    ],

    // ─── Streak at risk (evening, streak still salvageable) ──────────────
    streak_at_risk: [
        "hey… your streak is still alive. want to keep it going? 🔥",
        "psst — one small thing before midnight keeps the streak 🍡",
        "i believe in you. the streak's still yours if you want it.",
    ],

    // ─── Absence return — escalating across day 1 / 2 / 3+ missed ────────
    absence_day_1: [
        "i saved your spot 🥺",
    ],
    absence_day_2: [
        "i reorganized your tasks while you were gone 🍡",
    ],
    absence_day_3_plus: [
        "i'm not mad. i'm just… reorganizing again.",
    ],

    // ─── Win celebration (task / pomodoro / habit done) ──────────────────
    win_celebration: [
        "you actually did it. i knew you would 🍡",
        "that's one down. you're doing better than you think.",
        "nice. mochi noticed 🌸",
    ],

    // ─── Pomodoro focus phase complete ───────────────────────────────────
    pomodoro_complete: [
        "full focus on {task_title}. i felt that 🔥",
        "you locked in. that was beautiful 🍡",
        "focus done — go take a beat. you earned it 🌸",
    ],

    // ─── Permission granted (first-time toast) ───────────────────────────
    permission_granted: [
        "i can ping you now 🌸 i'll keep it gentle.",
        "we're connected. i won't be annoying, promise 🍡",
    ],
};

// ---------------------------------------------------------------------------
// Rotation logic — localStorage-backed, anti-repeat.
// ---------------------------------------------------------------------------
const LAST_KEY_PREFIX = "mochi.lastNotifPhrase.v1.";

function recallLast(type) {
    try { return localStorage.getItem(LAST_KEY_PREFIX + type) || ""; }
    catch { return ""; }
}
function rememberLast(type, line) {
    try { localStorage.setItem(LAST_KEY_PREFIX + type, line); }
    catch { /* private mode etc. */ }
}

// Picks a phrase for the given type, never the same as the previous one.
// Substitutes `{task_title}` (and any other placeholder you add later)
// from the `ctx` object. If the bank is missing a placeholder substitution,
// returns a clean generic line so we never leak a raw `{token}` to the user.
export function pickMochiPhrase(type, ctx = {}) {
    const bank = MOCHI_PHRASES[type];
    if (!bank || bank.length === 0) return null;
    const last = recallLast(type);
    const candidates = bank.filter((p) => p !== last);
    const pool = candidates.length > 0 ? candidates : bank;
    let line = pool[Math.floor(Math.random() * pool.length)];
    rememberLast(type, line);

    // Substitute placeholders. Safe fallbacks for missing context values.
    Object.keys(ctx).forEach((k) => {
        const v = ctx[k];
        if (v === undefined || v === null) return;
        line = line.replaceAll(`{${k}}`, String(v));
    });

    // Belt-and-suspenders: if a placeholder leaked because ctx didn't have
    // the key, swap to the closest plain phrase from the bank that doesn't
    // contain that placeholder. Prevents raw "{task_title}" reaching users.
    if (line.includes("{")) {
        const plain = bank.find((p) => !p.includes("{"));
        line = plain || line.replace(/\{[^}]+\}/g, "this");
    }
    return line;
}

// ---------------------------------------------------------------------------
// Dispatcher — single entry point. Title is always lowercase "mochi 🍡".
// Returns the Notification object on success, null otherwise. Never throws.
// ---------------------------------------------------------------------------
const MOCHI_TITLE = "mochi 🍡";

export function fireMochiNotification(type, ctx = {}, options = {}) {
    if (typeof window === "undefined" || !("Notification" in window)) return null;
    if (Notification.permission !== "granted") return null;
    const body = pickMochiPhrase(type, ctx);
    if (!body) return null;
    try {
        return new Notification(MOCHI_TITLE, {
            body,
            tag: options.tag,
            silent: options.silent ?? false,
            // Avatar large icon where supported — caller can override via
            // options.icon. We default to the app's manifest icon path.
            icon: options.icon || "/mochi-avatar-icon.png",
            // Android channel hint (no-op on iOS, harmless elsewhere).
            badge: options.badge || "/mochi-badge.png",
            requireInteraction: options.requireInteraction ?? false,
        });
    } catch {
        return null;
    }
}
